// Detect on a reduced decode, open the full image only when there's something to crop.
//
// Detection always runs at 640px (the models' input), so decoding a 24MP JPEG in full just to
// shrink it to 640 is wasted work on every frame with nobody in it. JPEG can be decoded at 1/2,
// 1/4 or 1/8 scale almost for free (DCT scaling) — WebCodecs' ImageDecoder does this when asked
// for a smaller size (~65 ms vs ~150 ms for a full decode of a 20MP baseline camera JPEG in
// Chromium; createImageBitmap's resize options do NOT: it decodes in full, then resizes, and
// progressive JPEGs gain nothing either way). Crops and the sharpness filter still come from the
// FULL-resolution image, exactly as before — this only changes what the 640px detector input is
// drawn from. Against the server's own input (cv2 INTER_AREA on the full image) the reduced
// input is as close as the full-decode one, and detects the same faces (test/parity-web).
//
// Pure helpers (unit-tested): read a JPEG's size + EXIF orientation from its header, plan the
// reduced decode, and decide adaptively whether the two-stage path is paying off on this batch
// (a gallery where nearly every frame has people pays for BOTH decodes, so it switches itself off).

/** Shortest edge the reduced decode must keep: the detectors' input size (square faces need it). */
export const DETECT_MIN_EDGE = 640;

const SOF = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);

/** Is this APP1 segment body the EXIF one ("Exif\0\0")? Others (XMP, …) carry no orientation tag here. */
const isExif = (b, start, len) => len >= 6 && b[start] === 0x45 && b[start + 1] === 0x78 && b[start + 2] === 0x69 && b[start + 3] === 0x66;

/** EXIF orientation (1–8) from an APP1 "Exif" segment body, or 1 if absent/unreadable. */
function exifOrientation(b, start, len) {
  // "Exif\0\0" then a TIFF header.
  if (len < 14 || b[start] !== 0x45 || b[start + 1] !== 0x78 || b[start + 2] !== 0x69 || b[start + 3] !== 0x66) return 1;
  const t = start + 6;
  const le = b[t] === 0x49 && b[t + 1] === 0x49;
  if (!le && !(b[t] === 0x4d && b[t + 1] === 0x4d)) return 1;
  const u16 = (o) => (le ? b[o] | (b[o + 1] << 8) : (b[o] << 8) | b[o + 1]);
  const u32 = (o) => (le ? (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16)) + b[o + 3] * 0x1000000 : b[o] * 0x1000000 + ((b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]));
  const end = start + len;
  const ifd = t + u32(t + 4);
  if (ifd + 2 > end) return 1;
  const n = u16(ifd);
  for (let k = 0; k < n; k += 1) {
    const e = ifd + 2 + k * 12;
    if (e + 12 > end) break;
    if (u16(e) === 0x0112) {
      const v = u16(e + 8);
      return v >= 1 && v <= 8 ? v : 1;
    }
  }
  return 1;
}

/**
 * Stored (pre-rotation) width/height, EXIF orientation and progressive flag of a JPEG, from its
 * first bytes.
 * Returns null for anything that isn't a readable baseline/progressive JPEG header — callers
 * then use the normal full decode.
 */
export function parseJpegInfo(bytes) {
  const b = bytes;
  if (!b || b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
  let orientation = 1;
  let exifSeen = false;
  let i = 2;
  while (i + 4 <= b.length) {
    if (b[i] !== 0xff) return null;
    const marker = b[i + 1];
    if (marker === 0xff) { i += 1; continue; } // fill byte
    i += 2;
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue; // standalone markers
    if (marker === 0xd9 || marker === 0xda) return null; // end / scan data before a frame header
    if (i + 2 > b.length) return null;
    const len = (b[i] << 8) | b[i + 1];
    if (len < 2) return null;
    // Orientation comes from the (first) EXIF APP1 only: cameras and editors often add an XMP APP1 after
    // it, which must not reset a portrait photo's orientation to 1.
    if (marker === 0xe1 && !exifSeen && i + len <= b.length && isExif(b, i + 2, len - 2)) {
      orientation = exifOrientation(b, i + 2, len - 2);
      exifSeen = true;
    }
    if (SOF.has(marker)) {
      if (i + 7 > b.length) return null;
      const height = (b[i + 3] << 8) | b[i + 4];
      const width = (b[i + 5] << 8) | b[i + 6];
      // Progressive frames (SOF2/6/10/14) can't be DCT-scaled cheaply — the decoder still has
      // to assemble every coefficient scan, so a "reduced" decode costs about a full one.
      const progressive = marker === 0xc2 || marker === 0xc6 || marker === 0xca || marker === 0xce;
      return width > 0 && height > 0 ? { width, height, orientation, progressive } : null;
    }
    i += len;
  }
  return null;
}

// Rotation (degrees clockwise) a decoder reports for each non-mirrored EXIF orientation.
const ROTATION = { 1: 0, 3: 180, 6: 90, 8: 270 };

/**
 * Plan the reduced decode for a JPEG: the smallest DCT scale (1/8, 1/4, 1/2) whose short edge
 * still has DETECT_MIN_EDGE pixels, the size to ask the decoder for, and the ORIENTED full-size
 * dimensions detections map back to. Null = no saving possible (small image, progressive JPEG)
 * or an orientation we don't handle (mirrored 2/4/5/7) → full decode.
 */
export function planScaledDecode(info, minEdge = DETECT_MIN_EDGE) {
  if (!info || info.progressive || !(info.orientation in ROTATION)) return null;
  const { width: w, height: h, orientation } = info;
  for (const s of [8, 4, 2]) {
    const dw = Math.ceil(w / s);
    const dh = Math.ceil(h / s);
    if (Math.min(dw, dh) >= minEdge) {
      const swap = orientation === 6 || orientation === 8;
      return {
        scale: s,
        desiredWidth: dw,
        desiredHeight: dh,
        rotation: ROTATION[orientation],
        fullW: swap ? h : w,
        fullH: swap ? w : h,
        smallW: swap ? dh : dw,
        smallH: swap ? dw : dh,
      };
    }
  }
  return null;
}

/**
 * Is the two-stage path paying off? Two-stage costs a reduced decode on every photo plus a full
 * decode on every photo with a detection; single-stage costs one full decode. So it wins while
 *   small + p·full < full   (p = share of photos with a face/person),
 * i.e. mostly-empty batches. Measures during a short warm-up, then decides from running averages
 * (updated by every photo either way) — and switches back if the batch changes character.
 */
export class TwoStagePolicy {
  constructor({ warmup = 6, margin = 0.9, alpha = 0.1 } = {}) {
    this.warmup = warmup;
    this.margin = margin;
    this.alpha = alpha;
    this.seen = 0;
    this.smallMs = null;
    this.fullMs = null;
    this.pNonEmpty = null;
  }

  shouldTry() {
    if (this.seen < this.warmup || this.smallMs == null) return true;
    if (this.fullMs == null) return true; // never needed a full decode — two-stage is winning outright
    const p = this.pNonEmpty ?? 1;
    return this.smallMs + p * this.fullMs < this.margin * this.fullMs;
  }

  /** Record one photo: `smallMs` when a reduced decode ran, `fullMs` when a full one did. */
  record({ smallMs = null, fullMs = null, nonEmpty }) {
    const ema = (prev, x) => (prev == null ? x : prev + this.alpha * (x - prev));
    this.seen += 1;
    if (smallMs != null) this.smallMs = ema(this.smallMs, smallMs);
    if (fullMs != null) this.fullMs = ema(this.fullMs, fullMs);
    this.pNonEmpty = ema(this.pNonEmpty, nonEmpty ? 1 : 0);
  }
}
